/**
 * Global Formatters Plugin
 * Provides global formatting methods for templates and components.
 * This plugin makes formatters available as global properties in Vue templates.
 */

import { useDateFormatter } from '@/composables/useDateFormatter'
import { useNumberFormatter } from '@/composables/useNumberFormatter'

export default {
  install(app) {
    // Global date formatting methods
    app.config.globalProperties.$formatDate = (date) => {
      const { formatDate } = useDateFormatter()
      return formatDate(date)
    }

    app.config.globalProperties.$formatDateTime = (date, options) => {
      const { formatDateTime } = useDateFormatter()
      return formatDateTime(date, options)
    }

    app.config.globalProperties.$formatTime = (date, options) => {
      const { formatTime } = useDateFormatter()
      return formatTime(date, options)
    }

    app.config.globalProperties.$formatTimeAgo = (date) => {
      const { formatTimeAgo } = useDateFormatter()
      return formatTimeAgo(date)
    }

    app.config.globalProperties.$formatShortDate = (date) => {
      const { formatShortDate } = useDateFormatter()
      return formatShortDate(date)
    }

    app.config.globalProperties.$formatLongDate = (date) => {
      const { formatLongDate } = useDateFormatter()
      return formatLongDate(date)
    }

    // Global number formatting methods
    app.config.globalProperties.$formatNumber = (value, options) => {
      const { formatNumber } = useNumberFormatter()
      return formatNumber(value, options)
    }

    app.config.globalProperties.$formatCurrency = (value, currency, options) => {
      const { formatCurrency } = useNumberFormatter()
      return formatCurrency(value, currency, options)
    }

    app.config.globalProperties.$formatInteger = (value, options) => {
      const { formatInteger } = useNumberFormatter()
      return formatInteger(value, options)
    }

    app.config.globalProperties.$formatDecimal = (value, options) => {
      const { formatDecimal } = useNumberFormatter()
      return formatDecimal(value, options)
    }

    app.config.globalProperties.$formatPercentage = (value, options) => {
      const { formatPercentage } = useNumberFormatter()
      return formatPercentage(value, options)
    }

    app.config.globalProperties.$formatCompactNumber = (value, options) => {
      const { formatCompactNumber } = useNumberFormatter()
      return formatCompactNumber(value, options)
    }

    app.config.globalProperties.$getCurrencySymbol = (currency) => {
      const { getCurrencySymbol } = useNumberFormatter()
      return getCurrencySymbol(currency)
    }

    // Global filters (for template syntax)
    app.config.globalProperties.$filters = {
      date: (date) => {
        const { formatDate } = useDateFormatter()
        return formatDate(date)
      },
      dateTime: (date, options) => {
        const { formatDateTime } = useDateFormatter()
        return formatDateTime(date, options)
      },
      number: (value, options) => {
        const { formatNumber } = useNumberFormatter()
        return formatNumber(value, options)
      },
      currency: (value, currency, options) => {
        const { formatCurrency } = useNumberFormatter()
        return formatCurrency(value, currency, options)
      }
    }

    // Provide formatters as injectable dependencies
    app.provide('dateFormatter', useDateFormatter)
    app.provide('numberFormatter', useNumberFormatter)
  }
}
